import { useState } from 'react';
import { platformApi, PlatformApiError } from '../lib/platformApi/client';
import { streamProvisioning, type StepEvent, type ProvisionCompleteEvent } from '../lib/platformApi/provisionStream';
import CredentialsRevealScreen from './CredentialsRevealScreen';

// Order drives the 4-segment progress bar. 'register' isn't its own segment
// — the POST that creates the row (step below) already covers it before the
// stream even opens — but 'schema'/'migrate'/'seed'/'healthcheck' each get
// one, matching the plan's confirmed 4-phase breakdown.
const PHASES: { step: string; label: string }[] = [
  { step: 'schema', label: 'Creating schema' },
  { step: 'migrate', label: 'Applying migrations' },
  { step: 'seed', label: 'Seeding master data' },
  { step: 'healthcheck', label: 'Verifying' },
];

interface ProvisioningWizardProps {
  onBack: () => void;
  onDone: () => void;
  /** Pre-filled values, e.g. from a website enquiry (website design §B8.2). */
  initial?: { name?: string; slug?: string };
  /** Shown above the form, e.g. "From enquiry ENQ-000012". */
  context?: string;
  /** Called once the hospital is live (before the credentials screen). */
  onProvisioned?: (tenant: { id: number; slug: string }) => void | Promise<void>;
  /** The back button's text (default "← Back to tenants"). */
  backLabel?: string;
}

type Phase = 'form' | 'provisioning' | 'credentials';

export default function ProvisioningWizard({ onBack, onDone, initial, context, onProvisioned, backLabel }: ProvisioningWizardProps) {
  const [phase, setPhase] = useState<Phase>('form');
  const [slug, setSlug] = useState(initial?.slug || '');
  const [name, setName] = useState(initial?.name || '');
  const [adminUser, setAdminUser] = useState('admin');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [currentStep, setCurrentStep] = useState<StepEvent | null>(null);
  const [log, setLog] = useState<string[]>([]);
  const [streamError, setStreamError] = useState('');
  const [result, setResult] = useState<ProvisionCompleteEvent | null>(null);
  const [tenantId, setTenantId] = useState<number | null>(null);

  const currentPhaseIndex = currentStep ? PHASES.findIndex(p => p.step === currentStep.step) : -1;

  const runProvisioning = async (id: number) => {
    setPhase('provisioning');
    try {
      await streamProvisioning(id, { adminUser: adminUser.trim() || undefined }, {
        onStep: (event) => { setCurrentStep(event); setLog(l => [...l, event.message]); },
        onComplete: (event) => {
          setResult(event); setPhase('credentials');
          if (onProvisioned) Promise.resolve(onProvisioned({ id, slug: slug.trim() })).catch(() => {});
        },
        onError: (event) => setStreamError(event.message),
      });
    } catch (e) {
      setStreamError(e instanceof Error ? e.message : String(e));
    }
  };

  const submit = async () => {
    if (!slug.trim() || !name.trim()) return;
    setSubmitting(true); setFormError('');
    try {
      const created = await platformApi.post<{ id: number; slug: string; resuming: boolean }>('/tenants', {
        slug: slug.trim(), name: name.trim(),
      });
      setTenantId(created.id);
      await runProvisioning(created.id);
    } catch (e) {
      setFormError(e instanceof PlatformApiError ? e.message : (e instanceof Error ? e.message : String(e)));
      setSubmitting(false);
    }
  };

  const retry = () => {
    if (!tenantId) return;
    setStreamError(''); setLog([]); setCurrentStep(null);
    runProvisioning(tenantId);
  };

  if (phase === 'credentials' && result) {
    return <CredentialsRevealScreen result={result} onDone={onDone} />;
  }

  if (phase === 'provisioning') {
    return (
      <div style={{ maxWidth: 560, margin: '60px auto', padding: '0 20px' }}>
        <div className="card" style={{ padding: 28 }}>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Provisioning '{slug}'</div>
          <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 24 }}>Stay on this page — this usually takes under a minute.</div>

          {/* 4-segment progress bar */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 18 }}>
            {PHASES.map((p, i) => (
              <div key={p.step} style={{
                flex: 1, height: 8, borderRadius: 4,
                background: i < currentPhaseIndex ? '#16A34A'
                  : i === currentPhaseIndex ? '#0B5FD6'
                  : '#E5E7EB',
                transition: 'background .2s ease',
              }} />
            ))}
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 16 }}>
            {currentStep ? PHASES.find(p => p.step === currentStep.step)?.label ?? currentStep.message : 'Starting…'}
          </div>

          <div style={{
            background: '#0F172A', color: '#94A3B8', fontFamily: 'var(--mono)', fontSize: 11,
            borderRadius: 8, padding: 12, maxHeight: 220, overflowY: 'auto',
          }}>
            {log.map((line, i) => <div key={i}>{line}</div>)}
          </div>

          {streamError && (
            <div style={{ marginTop: 16 }}>
              <div style={{ padding: '8px 12px', background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 8, color: '#991B1B', fontSize: 13, marginBottom: 10 }}>
                {streamError}
              </div>
              <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 10 }}>
                The tenant is left as "provisioning" and can be safely resumed — nothing was lost.
              </div>
              <button className="btn-primary" onClick={retry}>Resume provisioning</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // phase === 'form'
  return (
    <div style={{ maxWidth: 480, margin: '60px auto', padding: '0 20px' }}>
      <button onClick={onBack} style={{ marginBottom: 16, background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', padding: 0 }}>{backLabel || '← Back to tenants'}</button>
      <div className="card" style={{ padding: 28 }}>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: context ? 6 : 18 }}>Provision a new tenant</div>
        {context && <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 18 }}>{context}</div>}

        <div style={{ marginBottom: 14 }}>
          <label className="lbl">Slug (subdomain)</label>
          <input value={slug} onChange={e => setSlug(e.target.value.toLowerCase())} placeholder="apollo" autoFocus />
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>Lowercase letters, digits, hyphens. Becomes apollo.&lt;your-base-domain&gt;.</div>
        </div>
        <div style={{ marginBottom: 14 }}>
          <label className="lbl">Hospital name</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Apollo Clinic" />
        </div>
        <div style={{ marginBottom: 18 }}>
          <label className="lbl">First admin username</label>
          <input value={adminUser} onChange={e => setAdminUser(e.target.value)} placeholder="admin" />
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>Password is generated automatically and shown once after provisioning.</div>
        </div>

        {formError && (
          <div style={{ padding: '8px 12px', background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 8, color: '#991B1B', fontSize: 13, marginBottom: 14 }}>
            {formError}
          </div>
        )}

        <button className="btn-primary" style={{ width: '100%' }} onClick={submit} disabled={submitting || !slug.trim() || !name.trim()}>
          {submitting ? 'Starting…' : 'Provision tenant'}
        </button>
      </div>
    </div>
  );
}
